import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useFormContext, Controller } from 'react-hook-form';
import _get from 'lodash/get';

import { intlMessage } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { Dropdown as GenericDropdown } from 'components/Dropdown2';

import Label from '../Label/Label';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { makeValidators } from '../helpers';
import './Dropdown.scss';

const { block } = bem('hook-form-dropdown');

const LocalComponent = React.forwardRef((props, ref) => {
  const { value, onChange, options, dropdownProps, hasError, children } = props;
  const selectedOption = options && options.find(({ value: v }) => v === value);
  return (
    <GenericDropdown {...dropdownProps} selectedOption={selectedOption || {}}>
      {passThru => children({ ...passThru, value, onChange, hasError, ref })}
    </GenericDropdown>
  );
});

LocalComponent.propTypes = {
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.object, PropTypes.number]),
  onChange: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  options: PropTypes.arrayOf(PropTypes.any),
  dropdownProps: PropTypes.shape({}),
  hasError: PropTypes.bool,
  children: PropTypes.func.isRequired,
};

function Dropdown(props) {
  const {
    className,
    label,
    name,
    optional,
    defaultValue = '',
    children,
    hideErrorMessage,
    required,
    showRequiredLabel,
    ...dropdownProps
  } = props;
  const { options } = dropdownProps;
  const intl = useIntl();
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const validators = makeValidators(props, intl);
  const hasError = !!_get(errors, name, false);

  return (
    <div className={block(className, { hasError })}>
      <Label
        name={name}
        message={label}
        optional={optional}
        required={required}
        showRequiredLabel={showRequiredLabel}
      />
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue}
        rules={validators}
        render={({ field }) => (
          <LocalComponent {...field} options={options} dropdownProps={dropdownProps} hasError={hasError}>
            {children}
          </LocalComponent>
        )}
      />
      {!hideErrorMessage && <ErrorMessage name={name} errors={errors} />}
    </div>
  );
}

Dropdown.propTypes = {
  children: PropTypes.func.isRequired,
  className: PropTypes.string,
  defaultValue: PropTypes.string,
  hideErrorMessage: PropTypes.bool,
  label: intlMessage,
  name: PropTypes.string.isRequired,
  optional: PropTypes.bool,
  required: PropTypes.bool,
  showRequiredLabel: PropTypes.bool,
};

export default Dropdown;
